import { Matrix34 } from '@seddi/math-v2';
import { Box3, Sphere, Vector3 } from '@seddi/three';
import type { SceneDesc } from '@seddi/ui-three-viewer';

import { type BoundingSphere, type CameraLookAt, computeFromBounds } from './cameraLookAt';

/** Angle offset for headwear (front/back view) so the camera tilts to show the piece better. */
export const HEADWEAR_CAMERA_ANGLE_OFFSET = -Math.PI * 0.28;

export const computeSceneLookAt = (() => {
  const bbox = new Box3();
  const bsphere = new Sphere();
  const size = new Vector3();
  const m0 = new Matrix34();
  const m1 = new Matrix34();
  const m2 = new Matrix34();

  return (
    scene: Readonly<SceneDesc>,
    screenRatio: number = 1,
    angleY: number = 0,
  ): CameraLookAt | null => {
    const boundingBox = (scene as any).boundingBox;
    if (!boundingBox) {
      return null;
    }

    const { max, min } = boundingBox;
    bbox.set(min as Vector3, max as Vector3);

    // Axis-independent bounding box to sphere conversion
    bbox.getCenter(bsphere.center);
    bbox.getSize(size);
    const axis = Math.max(size.x, size.y, size.z);
    bsphere.radius = axis * 0.5;

    const bounds: BoundingSphere = {
      center: bsphere.center,
      radius: bsphere.radius,
    };

    const camera = computeFromBounds(bounds, screenRatio);

    if (angleY !== 0) {
      // Rotate camera origin around target (same as decorator computeCameraWithAngle).
      m0.makeTranslation(camera.target);
      m1.makeRotationY(angleY);
      m2.makeTranslationFromFloats(-camera.target.x, -camera.target.y, -camera.target.z);
      m0.multiplyInPlace(m1).multiplyInPlace(m2);
      m0.transformRef(camera.origin);
    }

    return camera;
  };
})();

